import React, { useEffect, useState, useRef } from "react";
import FormActionButton from "../shared-components/FormActionButton";
import { formPages, LENDER_LIST, MAKES_LIST } from "../../constants";
import Select from "../shared-components/Select";
import Label from "../shared-components/Label";
import Button from "../shared-components/Button";
import { DVLA_CHECK } from "../../constants";
import axios from "axios";
import { debounce } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLightbulb, faCar, faSquareParking, faBuilding, faCircleCheck, faArrowRotateLeft } from "@fortawesome/free-solid-svg-icons";
import "./AddressDetails.css";
import "./RequestFinanceLender.css";

export default function RequestFinanceLender({ compensate, onNext }) {
  // Multi-claim state
  const [claims, setClaims] = useState([
    {
      id: Date.now(),
      makeExpanded: false,
      regExpanded: false,
      lenderExpanded: false,
      make: "",
      vehicle_reg: "",
      lender_name: "",
      dvla_data: null,
      dvla_error: false,
      error: "",
    },
  ]);

  const [lenderList, setLenderList] = useState([]);
  const [makesList, setMakesList] = useState([]);
  const [showTipModal, setShowTipModal] = useState(false);
  const [deletingIds, setDeletingIds] = useState(new Set());

  const dvlaSearchRefs = useRef({});

  // Claim operations
  const updateClaim = (claimId, fields) =>
    setClaims((prev) => prev.map((c) => (c.id === claimId ? { ...c, ...fields } : c)));

  const deleteClaim = (claimId) => {
    setDeletingIds((prev) => new Set(prev).add(claimId));
    setTimeout(() => {
      setClaims((prev) => {
        const updated = prev.filter((c) => c.id !== claimId);
        if (updated.length === 0) {
          return [{
            id: Date.now(),
            makeExpanded: false, regExpanded: false, lenderExpanded: false,
            make: "", vehicle_reg: "", lender_name: "",
            dvla_data: null, dvla_error: false, regTyping: false, error: "",
          }];
        }
        return updated;
      });
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(claimId);
        return next;
      });
    }, 100);
  };

  const addClaim = () =>
    setClaims((prev) => [
      ...prev,
      {
        id: Date.now(),
        makeExpanded: false,
        regExpanded: false,
        lenderExpanded: false,
        make: "",
        vehicle_reg: "",
        lender_name: "",
        dvla_data: null,
        dvla_error: false,
        regTyping: false,
        error: "",
      },
    ]);

  // Per-claim debounced DVLA search
  const getDebouncedDvlaSearch = (claimId) => {
    if (!dvlaSearchRefs.current[claimId]) {
      dvlaSearchRefs.current[claimId] = debounce(async (reg) => {
        const stripped = reg.replace(/ /g, "");
        if (!stripped || stripped.length < 2) {
          updateClaim(claimId, { dvla_data: null, dvla_error: false });
          return;
        }
        try {
          const response = await axios.post(DVLA_CHECK(), {
            registrationNumber: stripped,
          });
          updateClaim(claimId, { dvla_data: response.data, dvla_error: false });
        } catch {
          updateClaim(claimId, { dvla_data: null, dvla_error: true });
        }
      }, 600);
    }
    return dvlaSearchRefs.current[claimId];
  };

  useEffect(() => {
    try {
      const fetchList = async () => {
        const fetchedMakes = await MAKES_LIST();
        setMakesList(fetchedMakes);
        const fecthedLenders = await LENDER_LIST();
        setLenderList(fecthedLenders);
      };
      fetchList();
    } catch (error) {
      console.error("error fetching lists", error);
      throw error;
    }
  }, []);

  // Cancel all debounced searches on unmount
  useEffect(() => {
    return () => {
      Object.values(dvlaSearchRefs.current).forEach((fn) => {
        if (fn && fn.cancel) fn.cancel();
      });
    };
  }, []);

  const isFormValid = () => {
    // At least one claim must have at least one field filled
    return (
      claims.every((c) => c.make || c.vehicle_reg || c.lender_name) &&
      claims.some((c) => c.make || c.vehicle_reg || c.lender_name)
    );
  };

  const toggleExpand = (claimId, optionType) => {
    setClaims((prev) =>
      prev.map((c) => {
        if (c.id !== claimId) return c;
        return {
          ...c,
          makeExpanded: optionType === "make" ? !c.makeExpanded : false,
          regExpanded: optionType === "reg" ? !c.regExpanded : false,
          lenderExpanded: optionType === "lender" ? !c.lenderExpanded : false,
        };
      })
    );
  };

  const ChevronIcon = ({ expanded }) => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
        transition: "transform 0.3s ease",
      }}
    >
      <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
  );

  const resetClaim = (claimId, optionType) => {
    const fields = {};
    if (optionType === "make") fields.make = "";
    if (optionType === "reg") { fields.vehicle_reg = ""; fields.dvla_data = null; fields.dvla_error = false; }
    if (optionType === "lender") fields.lender_name = "";
    updateClaim(claimId, fields);
  };

  const renderOptionCard = (claimId, optionType, icon, label, expanded, filled, disabled, content) => (
    <div className={`option-card option-card--${optionType}${disabled ? " option-card--disabled" : ""}`} key={optionType}>
      <div
        className="option-card-header"
        onClick={() => !disabled && toggleExpand(claimId, optionType)}
        style={{ cursor: disabled ? "not-allowed" : "pointer" }}
      >
        <div className="option-header-content">
          <div className="option-header-row">
            <span className="option-number">
              <FontAwesomeIcon icon={icon} />
            </span>
            <span className="option-label">{label}</span>
            {filled && (
              <span className="option-check">
                <FontAwesomeIcon icon={faCircleCheck} />
              </span>
            )}
            {filled && (
              <button
                className="option-reset-btn"
                onClick={(e) => { e.stopPropagation(); resetClaim(claimId, optionType); }}
                aria-label="Reset"
              >
                <FontAwesomeIcon icon={faArrowRotateLeft} />
              </button>
            )}
            {!filled && (
              <span className="option-chevron">
                <ChevronIcon expanded={expanded} />
              </span>
            )}
          </div>
        </div>
      </div>
      {expanded && <div className="option-card-body">{content}</div>}
    </div>
  );

  const renderMakeCardContent = (claimId) => {
    const claim = claims.find((c) => c.id === claimId);
    return (
      <>
        <Select
          options={[...makesList]}
          onChange={(value) => {
            updateClaim(claimId, { make: value?.value || value });
          }}
          value={claim?.make || ""}
          placeholder={"Select vehicle make"}
          divClassname={"car-finance"}
        />
      </>
    );
  };

  const renderRegCardContent = (claimId) => {
    const claim = claims.find((c) => c.id === claimId);
    return (
      <>
        <input
          type="text"
          className="vehicle-reg-input"
          value={claim?.vehicle_reg || ""}
          onChange={(e) => {
            const raw = e.target.value
              .toUpperCase()
              .replace(/[^A-Z0-9 ]/g, "")
              .replace(/^ /, "")
              .replace(/ {2,}/g, " ");
            // max 7 alphanumeric chars (space doesn't count toward limit)
            const alphanum = raw.replace(/ /g, "");
            if (alphanum.length > 7) return;
            updateClaim(claimId, {
              vehicle_reg: raw,
              dvla_data: null,
              dvla_error: false,
            });
            getDebouncedDvlaSearch(claimId)(raw);
          }}
          placeholder="Enter registration number"
          style={{
            padding: "10px 20px",
            border: "none",
            boxShadow: "inset 0 0 0 1px #000000",
            borderRadius: "10px",
            width: "100%",
            fontWeight: 700,
            boxSizing: "border-box",
            marginTop: "10px",
          }}
        />

        {claim?.dvla_data && (
          <div
            style={{
              marginTop: "10px",
              padding: "10px",
              border: "1px solid #28a745",
              borderRadius: "10px",
              color: "#155724",
              backgroundColor: "#d4edda",
            }}
          >
            <strong>Vehicle Found:</strong> {claim.dvla_data.make} -{" "}
            {claim.dvla_data.colour}
          </div>
        )}
      </>
    );
  };

  const renderLenderCardContent = (claimId) => {
    const claim = claims.find((c) => c.id === claimId);
    return (
      <>
        <Select
          options={[...lenderList]}
          onChange={(value) => {
            updateClaim(claimId, { lender_name: value?.value || value });
          }}
          value={claim?.lender_name || ""}
          placeholder={"Select finance lender"}
          divClassname={"car-finance"}
        />
      </>
    );
  };

  const buildPayload = () => {
    const vehicleRegistrationNumbers = claims
      .filter((c) => c.vehicle_reg)
      .map((c) => c.vehicle_reg);

    const validClaims = claims
      .filter((c) => c.make || c.lender_name)
      .map((c) => ({
        lender_name: c.lender_name || "",
        finance_agreement_number: "",
        date_finance_was_taken: "",
        account_type: "",
        make: c.make || "",
        new_or_used: "",
      }));

    return {
      claims: [...(compensate?.claims || []), ...validClaims],
      ...(vehicleRegistrationNumbers.length > 0 && {
        vehicleRegistrationNumbers,
      }),
    };
  };

  return (
    <>
      {showTipModal && (
        <div
          className="address-modal-overlay"
          onClick={() => setShowTipModal(false)}
        >
          <div
            className="address-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="address-modal-header">
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  backgroundColor: "var(--brand-primary)",
                  flexShrink: 0,
                  marginRight: "8px",
                }}
              >
                <FontAwesomeIcon
                  icon={faLightbulb}
                  style={{ color: "#fff", fontSize: "16px" }}
                />
              </span>
              <span className="address-modal-title">
                Tip to find your claims faster
              </span>
            </div>

            <p className="address-modal-description">
              Providing your vehicle details (make, registration, or finance
              lender) helps us locate all your claims more quickly and
              accurately.
            </p>

            <p
              style={{
                fontWeight: "700",
                fontSize: "15px",
                margin: "0 0 16px 0",
              }}
            >
              Would you like to add this information now?
            </p>

            <div className="address-modal-actions">
              <button
                className="modal-btn-primary"
                onClick={() => setShowTipModal(false)}
              >
                Select details
              </button>
              <button
                className="modal-btn-secondary"
                onClick={() => {
                  setShowTipModal(false);
                  onNext(buildPayload(), formPages.Success);
                }}
              >
                Continue without
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className="d-flex justify-content-center align-items-center heading-icon fdc-form"
        data-testid="page-request-finance"
        style={{ paddingBottom: "5px" }}
      >
        <h2 className="title">
          Add your vehicle make or lender to help us find every claim.
        </h2>
      </div>

      <div className="fdc-form gif-input">
        <div className="row">
          <div className="col-md-12">
            {/* Claims */}
            {claims.map((claim, index) => (
              <div
                key={claim.id}
                className={`claim-block${deletingIds.has(claim.id) ? " claim-block--deleting" : ""}`}
              >
                <div className="claim-block-header">
                  <div className="claim-block-title">Claim {index + 1}</div>
                  {claims.length > 1 && (
                    <button
                      className="btn-delete"
                      onClick={() => deleteClaim(claim.id)}
                      aria-label="Remove"
                    >
                      <img src="/icons/lucid-icons/trash.svg" alt="" />
                    </button>
                  )}
                </div>

                <div className="option-columns">
                  {renderOptionCard(
                    claim.id,
                    "make",
                    faCar,
                    "Select your Vehicle's Make",
                    claim.makeExpanded,
                    !!claim.make,
                    !!(claim.vehicle_reg || claim.lender_name),
                    renderMakeCardContent(claim.id)
                  )}
                  {renderOptionCard(
                    claim.id,
                    "reg",
                    faSquareParking,
                    "Enter your Vehicle's Registration",
                    claim.regExpanded,
                    !!claim.vehicle_reg,
                    !!(claim.make || claim.lender_name),
                    renderRegCardContent(claim.id)
                  )}
                  {renderOptionCard(
                    claim.id,
                    "lender",
                    faBuilding,
                    "Select your Finance Lender",
                    claim.lenderExpanded,
                    !!claim.lender_name,
                    !!(claim.make || claim.vehicle_reg),
                    renderLenderCardContent(claim.id)
                  )}
                </div>

                <div className="info-banner">
                  <div className="info-banner-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="12" y1="16" x2="12" y2="12"></line>
                      <line x1="12" y1="8" x2="12.01" y2="8"></line>
                    </svg>
                  </div>
                  <div className="info-banner-text">
                    You only need to complete <strong style={{textDecoration: "underline"}}>ONE</strong> option above.
                  </div>
                </div>

                {claim.error && (
                  <div className="claim-block-error">{claim.error}</div>
                )}
              </div>
            ))}

            {/* Add another claim button */}
            <div className="add-claim-btn-wrapper">
              <Button
                onClick={addClaim}
                label="+ Add another claim"
                testId="request-finance-add-claim"
                style={{
                  width: "100%",
                  height: "53px",
                  backgroundColor: "var(--brand-primary)",
                  color: "var(--brand-button-text, #fff)",
                  borderRadius: "10px",
                  border: "none",
                  fontWeight: "700",
                  fontSize: "14px",
                }}
              />
            </div>

            {/* Footer tip */}
            <div className="footer-tip">
              <div className="footer-tip-icon">
                <FontAwesomeIcon icon={faLightbulb} />
              </div>
              <div className="footer-tip-text">
                Not sure which option to use? Any of these details can help us
                identify your vehicle. Start with the information you have
                available.
              </div>
            </div>

            <div className="col-md-6 mx-auto text-center">
              <FormActionButton
                nextTitle="Next"
                nextTestId="request-finance-btn-next"
                onNext={() => {
                  if (isFormValid()) {
                    onNext(buildPayload(), formPages.Success);
                  } else {
                    setShowTipModal(true);
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
